import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Timeline,
  Upload,
  message,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import type { ColumnsType } from 'antd/es/table';
import {
  AuditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileSearchOutlined,
  PayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  SearchOutlined,
  SendOutlined,
  StopOutlined,
  UploadOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { API_ENDPOINTS, buildApiUrl } from '../../api/config';
import { get, post, request } from '../../api/request';
import './OperationAdminPages.css';

type OrderStatus = 'paid' | 'pending' | 'refund_pending' | 'refunded' | 'refund_rejected' | 'closed';
type WorkflowStage = '灵感' | '设定' | '人物' | '大纲' | '章节' | '审稿' | '发布';
type BookStatus = '已上架' | '审核中' | '草稿' | '已下架';

interface OrderItem {
  id: string;
  user: string;
  product: string;
  channel: string;
  amount: number;
  ink: number;
  status: OrderStatus;
  paidAt: string;
  source: string;
}

interface InkAccount {
  id: string;
  userId?: number;
  user: string;
  email?: string;
  avatar: string;
  balance: number;
  frozen: number;
  consumedToday: number;
  lastChange: string;
  risk: string;
}

interface InkRecord {
  id: string;
  user: string;
  type: string;
  amount: number;
  operator: string;
  reason: string;
  createdAt: string;
  cost_yuan?: number;
  model?: string;
  cost_source?: string;
}

interface BookTransferResult {
  title: string;
  status: 'imported' | 'skipped' | 'failed';
  message: string;
}

interface BookExportPart {
  index: number;
  filename: string;
  size: number;
  book_count: number;
  downloaded: boolean;
  deleted: boolean;
}

interface BookExportTask {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'downloaded';
  progress: number;
  message: string;
  total_books: number;
  processed_books: number;
  total_chapters: number;
  processed_chapters: number;
  current_title: string;
  download_token: string;
  parts: BookExportPart[];
}

interface WorkflowProject {
  id: string;
  project_id?: number;
  title: string;
  user: string;
  stage: WorkflowStage;
  savedStages: WorkflowStage[];
  progress: number;
  words: number;
  chapterCount?: number;
  lastSaved: string;
  risk: string;
  summary: string;
}

interface BookAdminItem {
  id: string;
  project_id?: number;
  slug?: string;
  title: string;
  author: string;
  cover: string;
  status: BookStatus;
  reader: string;
  category: string;
  words: string;
  chapters: number;
  lockedChapters: number;
  paywallEnabled: boolean;
  paywallMode: 'range' | 'selected';
  paidFromChapter: number;
  paidChapterNos: number[];
  chapterPrice: number;
  income: number;
  aiCost: number;
  createdAt: string;
  updatedAt: string;
  storageSource: string;
  storageProviderType: 'minio' | 'file_service';
  storageObjectCount: number;
}

interface BookCategoryStat {
  id: string;
  channel: '男频' | '女频';
  category: string;
  count: number;
}

interface BookChapterItem {
  id: number;
  chapter_no: number;
  title: string;
  content?: string;
  status: string;
  words: number;
  updated_at?: string;
}

interface NovelUploadTask {
  id: number;
  filename: string;
  size: number;
  status: 'pending' | 'running' | 'completed' | 'skipped' | 'failed';
  message: string;
  title?: string;
  author?: string;
  chapter_count?: number;
  word_count?: number;
  encoding?: string;
  created_at: string;
}

interface LibraryRankingAdminItem {
  id?: string;
  rank: number;
  channel: '男频' | '女频' | '新书';
  slug: string;
  title: string;
  status: string;
  update: string;
  enabled: boolean;
  author?: string;
  cover_url?: string;
  project_id?: number;
  score?: number;
  read_count?: number;
  like_count?: number;
  favorite_count?: number;
}

type LibrarySectionKey = 'male_featured' | 'female_featured' | 'copyright' | 'activity_wall';

interface LibrarySectionAdminItem {
  id?: string;
  section: LibrarySectionKey;
  sort: number;
  slug: string;
  enabled: boolean;
  title?: string;
  author?: string;
  cover_url?: string;
  available?: boolean;
}

interface SiteNewsAdminItem {
  id?: string;
  sort: number;
  date: string;
  title: string;
  desc: string;
  content: string;
  enabled: boolean;
}

interface BookshelfAdminItem {
  id: string;
  user_id: number;
  username: string;
  slug: string;
  title: string;
  cover: string;
  added_at?: string;
}

interface LibraryCategoryAdminItem {
  id?: string;
  sort: number;
  channel: '男频' | '女频';
  name: string;
  enabled: boolean;
}

const stageOrder: WorkflowStage[] = ['灵感', '设定', '人物', '大纲', '章节', '审稿', '发布'];

const orderStatusMeta: Record<OrderStatus, { color: string; text: string }> = {
  paid: { color: 'success', text: '已支付' },
  pending: { color: 'processing', text: '待支付' },
  refund_pending: { color: 'processing', text: '退款审核中' },
  refunded: { color: 'warning', text: '已退款' },
  refund_rejected: { color: 'error', text: '退款已驳回' },
  closed: { color: 'default', text: '已关闭' },
};

const bookStatusColor: Record<BookStatus, string> = {
  已上架: 'green',
  审核中: 'blue',
  草稿: 'default',
  已下架: 'red',
};

interface ListResponse<T> {
  data: T[];
  total: number;
}

interface OperationOverviewResponse {
  metrics: {
    todayIncome: number;
    inkTotal: number;
    workflowCount: number;
    pendingBooks: number;
  };
  workflows: WorkflowProject[];
  books: BookAdminItem[];
  orders: OrderItem[];
}

const PageHeader: React.FC<{ title: string; desc: string; actions?: React.ReactNode }> = ({ title, desc, actions }) => (
  <div className="ops-page-header">
    <div>
      <h1 className="page-title">{title}</h1>
      <p>{desc}</p>
    </div>
    {actions && <div className="ops-page-actions">{actions}</div>}
  </div>
);

const StageDots: React.FC<{ savedStages: WorkflowStage[]; current: WorkflowStage }> = ({ savedStages, current }) => (
  <div className="stage-dots">
    {stageOrder.map((stage) => {
      const done = savedStages.includes(stage);
      const active = current === stage;
      return (
        <span className={done ? 'done' : active ? 'active' : ''} key={stage}>
          {stage}
        </span>
      );
    })}
  </div>
);

export const OperationOverviewPage: React.FC = () => {
  const [overview, setOverview] = useState<OperationOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      setOverview(await get<OperationOverviewResponse>(API_ENDPOINTS.operations.overview));
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载运营概览失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const metrics = overview?.metrics || {
    todayIncome: 0,
    inkTotal: 0,
    workflowCount: 0,
    pendingBooks: 0,
  };
  const overviewWorkflows = overview?.workflows || [];
  const overviewBooks = overview?.books || [];

  return (
    <div className="ops-page">
      <PageHeader
        title="运营概览"
        desc="从收入、墨水、创作流程和书阁内容四个方向观察平台运营状态。"
        actions={<Button icon={<ReloadOutlined />} loading={loading} onClick={loadOverview}>刷新数据</Button>}
      />

      <Row gutter={[16, 16]} className="ops-metric-row">
        <Col xs={24} sm={12} xl={6}>
          <Card bordered={false} className="ops-stat-card">
            <Statistic title="今日实付收入" prefix="¥" value={metrics.todayIncome} precision={2} />
            <Tag color="green"><RiseOutlined /> 较昨日 +12.8%</Tag>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card bordered={false} className="ops-stat-card">
            <Statistic title="平台墨水余额" value={metrics.inkTotal} suffix="墨水" />
            <Tag color="blue"><WalletOutlined /> 后台可划转</Tag>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card bordered={false} className="ops-stat-card">
            <Statistic title="创作中项目" value={metrics.workflowCount} suffix="本" />
            <Tag color="purple"><FileSearchOutlined /> 1 本需要干预</Tag>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card bordered={false} className="ops-stat-card">
            <Statistic title="待审核书籍" value={metrics.pendingBooks} suffix="本" />
            <Tag color="orange"><AuditOutlined /> 发布前审核</Tag>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={15}>
          <Card bordered={false} title="核心运营节奏" className="ops-panel-card">
            <div className="ops-rail-chart">
              {['订单支付', '墨水消耗', 'AI生成', '作品发布', '锁章收入', '退款售后'].map((label, index) => (
                <div className="ops-rail-item" key={label}>
                  <span style={{ height: `${72 + index * 16}px` }} />
                  <strong>{label}</strong>
                  <em>{[128, 356, 924, 18, 73, 4][index]}</em>
                </div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={9}>
          <Card bordered={false} title="今日待办" className="ops-panel-card">
            <Timeline
              items={[
                { color: 'blue', children: '审核 1 本新提交到书阁的作品' },
                { color: 'green', children: '复核 2 条后台墨水划转记录' },
                { color: 'orange', children: '处理 1 个创作流程中断项目' },
                { color: 'red', children: '检查退款后墨水扣回是否完成' },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="ops-section-row">
        <Col xs={24} xl={12}>
          <Card bordered={false} title="高风险创作项目" className="ops-panel-card">
            {overviewWorkflows.filter((item) => item.risk !== '正常').map((item) => (
              <div className="ops-risk-item" key={item.id}>
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.summary}</p>
                </div>
                <Tag color="orange">{item.risk}</Tag>
              </div>
            ))}
            {!overviewWorkflows.filter((item) => item.risk !== '正常').length && <div className="ops-muted">暂无高风险项目</div>}
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card bordered={false} title="书阁发布概况" className="ops-panel-card">
            {overviewBooks.map((book) => (
              <div className="ops-book-line" key={book.id}>
                <img src={book.cover} alt={book.title} />
                <div>
                  <strong>{book.title}</strong>
                  <span>{book.category} · {book.words} · {book.chapters} 章</span>
                </div>
                <Tag color={bookStatusColor[book.status]}>{book.status}</Tag>
              </div>
            ))}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export const OrderManagementPage: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<OrderStatus | 'all'>('all');
  const [dataSource, setDataSource] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(false);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = buildApiUrl(API_ENDPOINTS.operations.orders, { keyword, status });
      const result = await get<ListResponse<OrderItem>>(endpoint);
      setDataSource(result.data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载订单失败');
    } finally {
      setLoading(false);
    }
  }, [keyword, status]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const columns: ColumnsType<OrderItem> = [
    { title: '订单号', dataIndex: 'id', width: 170, fixed: 'left', render: (value) => <span className="ops-code">{value}</span> },
    { title: '用户', dataIndex: 'user', width: 110 },
    { title: '商品', dataIndex: 'product', width: 140 },
    { title: '支付渠道', dataIndex: 'channel', width: 110 },
    { title: '金额', dataIndex: 'amount', width: 100, render: (value: number) => <strong>¥{value.toFixed(2)}</strong> },
    { title: '到账墨水', dataIndex: 'ink', width: 110, render: (value: number) => `${value.toLocaleString()} 墨水` },
    { title: '状态', dataIndex: 'status', width: 100, render: (value: OrderStatus) => {
      const meta = orderStatusMeta[value] || { color: 'default', text: String(value || '未知') };
      return <Tag color={meta.color}>{meta.text}</Tag>;
    } },
    { title: '来源', dataIndex: 'source', width: 120 },
    { title: '支付时间', dataIndex: 'paidAt', width: 160 },
    {
      title: '操作',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => Modal.info({ title: record.id, content: `用户 ${record.user} 购买 ${record.product}` })}>详情</Button>
      ),
    },
  ];

  return (
    <div className="ops-page">
      <PageHeader
        title="订单管理"
        desc="管理价格页、阅读页和后台补链产生的支付订单，支持按用户、状态和时间筛选。"
        actions={<Button type="primary" icon={<PayCircleOutlined />}>导出订单</Button>}
      />

      <Card bordered={false} className="ops-filter-card">
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="订单号 / 用户昵称"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            style={{ width: 240 }}
          />
          <Select
            value={status}
            onChange={setStatus}
            style={{ width: 150 }}
            options={[
              { label: '全部状态', value: 'all' },
              { label: '已支付', value: 'paid' },
              { label: '待支付', value: 'pending' },
              { label: '退款审核中', value: 'refund_pending' },
              { label: '已退款', value: 'refunded' },
              { label: '退款已驳回', value: 'refund_rejected' },
              { label: '已关闭', value: 'closed' },
            ]}
          />
          <DatePicker.RangePicker />
          <Button icon={<SearchOutlined />} loading={loading} onClick={loadOrders}>查询</Button>
        </Space>
      </Card>

      <Card bordered={false}>
        <Table rowKey="id" columns={columns} dataSource={dataSource} loading={loading} scroll={{ x: 1260 }} pagination={{ pageSize: 8 }} />
      </Card>
    </div>
  );
};

export const InkAccountPage: React.FC = () => {
  const [accounts, setAccounts] = useState<InkAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountKeyword, setAccountKeyword] = useState('');
  const [accountRisk, setAccountRisk] = useState<'all' | string>('all');
  const [accountDateRange, setAccountDateRange] = useState<[string, string] | null>(null);

  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const accountResult = await get<ListResponse<InkAccount>>(buildApiUrl(API_ENDPOINTS.operations.inkAccounts, {
        keyword: accountKeyword,
        risk: accountRisk,
        date_from: accountDateRange?.[0],
        date_to: accountDateRange?.[1],
      }));
      setAccounts(accountResult.data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载墨水账户失败');
    } finally {
      setAccountsLoading(false);
    }
  }, [accountDateRange, accountKeyword, accountRisk]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const accountColumns: ColumnsType<InkAccount> = [
    {
      title: '用户',
      dataIndex: 'user',
      render: (_, record) => (
        <Space>
          <Avatar src={record.avatar} />
          <div>
            <strong>{record.user}</strong>
            <div className="ops-muted">{record.id}{record.email ? ` · ${record.email}` : ''}</div>
          </div>
        </Space>
      ),
    },
    { title: '可用墨水', dataIndex: 'balance', render: (value: number) => <strong>{value.toLocaleString()} 墨水</strong> },
    { title: '冻结墨水', dataIndex: 'frozen', render: (value: number) => `${value.toLocaleString()} 墨水` },
    { title: '今日消耗', dataIndex: 'consumedToday', render: (value: number) => `${value.toLocaleString()} 墨水` },
    { title: '最近变动', dataIndex: 'lastChange' },
    { title: '状态', dataIndex: 'risk', render: (value: string) => <Tag color={value === '正常' ? 'green' : 'orange'}>{value}</Tag> },
  ];

  return (
    <div className="ops-page">
      <PageHeader title="墨水账户" desc="查看用户可用墨水、冻结墨水、今日消耗和最近变动，支持按用户、状态与时间筛选。" />

      <Card
        bordered={false}
        title="墨水账户"
        className="ops-panel-card ops-section-row"
        extra={<Button icon={<ReloadOutlined />} loading={accountsLoading} onClick={loadAccounts}>刷新账户</Button>}
      >
        <Card bordered={false} className="ops-filter-card ops-inner-filter-card">
          <Space wrap>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="用户昵称 / 邮箱 / 用户ID"
              value={accountKeyword}
              onChange={(event) => setAccountKeyword(event.target.value)}
              style={{ width: 240 }}
            />
            <Select
              value={accountRisk}
              onChange={setAccountRisk}
              style={{ width: 150 }}
              options={[
                { label: '全部状态', value: 'all' },
                { label: '正常', value: '正常' },
                { label: '大额消耗', value: '大额消耗' },
              ]}
            />
            <DatePicker.RangePicker
              onChange={(_, dateStrings) => {
                const [start, end] = dateStrings as [string, string];
                setAccountDateRange(start && end ? [start, end] : null);
              }}
            />
            <Button icon={<SearchOutlined />} loading={accountsLoading} onClick={loadAccounts}>查询账户</Button>
          </Space>
        </Card>
        <Table rowKey="id" columns={accountColumns} dataSource={accounts} loading={accountsLoading} pagination={{ pageSize: 8 }} scroll={{ x: 920 }} />
      </Card>
    </div>
  );
};

export const InkTransferPage: React.FC = () => {
  const [form] = Form.useForm();
  const [transferUsers, setTransferUsers] = useState<InkAccount[]>([]);
  const [transferUsersLoading, setTransferUsersLoading] = useState(false);
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  const userOptions = transferUsers.map((item) => ({
    label: `${item.user}${item.email ? ` / ${item.email}` : ''} / ${item.id}`,
    value: item.userId,
  }));

  const loadTransferUsers = useCallback(async () => {
    setTransferUsersLoading(true);
    try {
      const result = await get<ListResponse<InkAccount>>(buildApiUrl(API_ENDPOINTS.operations.inkAccounts));
      setTransferUsers(result.data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载用户列表失败');
    } finally {
      setTransferUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTransferUsers();
  }, [loadTransferUsers]);

  const submitTransfer = async () => {
    const values = await form.validateFields();
    try {
      setTransferSubmitting(true);
      await post(API_ENDPOINTS.operations.inkTransfers, {
        user_id: values.user,
        type: values.type,
        amount: values.amount,
        reason: values.reason,
      });
      message.success('划转已入账');
      form.resetFields();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '划转失败');
    } finally {
      setTransferSubmitting(false);
    }
  };

  return (
    <div className="ops-page">
      <PageHeader title="后台划转" desc="按展示墨水给用户赠送、回收或冻结；系统会自动换算为内部计费点并写入订单和流水。" />

      <Card bordered={false} title="后台划转" className="ops-panel-card ops-section-row">
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={8}>
            <Form form={form} layout="vertical">
              <Form.Item name="user" label="目标用户" rules={[{ required: true, message: '请选择用户' }]}>
                <Select
                  loading={transferUsersLoading}
                  showSearch
                  optionFilterProp="label"
                  placeholder="选择用户"
                  options={userOptions}
                />
              </Form.Item>
              <Form.Item name="type" label="划转类型" rules={[{ required: true, message: '请选择类型' }]}>
                <Select options={[{ label: '赠送墨水', value: 'grant' }, { label: '扣减墨水', value: 'deduct' }, { label: '冻结墨水', value: 'freeze' }]} />
              </Form.Item>
              <Form.Item name="amount" label="墨水数量" rules={[{ required: true, message: '请输入数量' }]}>
                <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="例如 200" />
              </Form.Item>
              <Form.Item name="reason" label="操作原因" rules={[{ required: true, message: '请填写原因' }]}>
                <Input.TextArea rows={3} maxLength={80} showCount placeholder="活动补偿、售后处理、异常扣回等" />
              </Form.Item>
              <Button type="primary" block icon={<SendOutlined />} loading={transferSubmitting} onClick={submitTransfer}>生成划转记录</Button>
            </Form>
          </Col>
          <Col xs={24} xl={16}>
            <div className="ops-transfer-tip">
              <Descriptions column={1} size="small" title="操作说明">
                <Descriptions.Item label="计费口径">用户端按 1 元 = 10 墨水展示，底层仍按真实 token 成本结算。</Descriptions.Item>
                <Descriptions.Item label="赠送墨水">直接增加用户可用墨水余额，同时写入订单和流水。</Descriptions.Item>
                <Descriptions.Item label="官方回收">从用户可用余额中回收，不会扣成负数。</Descriptions.Item>
                <Descriptions.Item label="冻结墨水">增加冻结额度，便于处理异常订单或争议场景。</Descriptions.Item>
              </Descriptions>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export const InkRecordPage: React.FC = () => {
  const [records, setRecords] = useState<InkRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordKeyword, setRecordKeyword] = useState('');
  const [recordType, setRecordType] = useState<'all' | string>('all');
  const [recordDateRange, setRecordDateRange] = useState<[string, string] | null>(null);

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const recordResult = await get<ListResponse<InkRecord>>(buildApiUrl(API_ENDPOINTS.operations.inkRecords, {
        keyword: recordKeyword,
        type: recordType,
        date_from: recordDateRange?.[0],
        date_to: recordDateRange?.[1],
      }));
      setRecords(recordResult.data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载墨水流水失败');
    } finally {
      setRecordsLoading(false);
    }
  }, [recordDateRange, recordKeyword, recordType]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const recordColumns: ColumnsType<InkRecord> = [
    { title: '流水号', dataIndex: 'id', render: (value) => <span className="ops-code">{value}</span> },
    { title: '用户', dataIndex: 'user' },
    { title: '类型', dataIndex: 'type', render: (value) => <Tag color={value.includes('消耗') || value.includes('扣') ? 'red' : 'blue'}>{value}</Tag> },
    { title: '墨水变动', dataIndex: 'amount', render: (value: number) => <strong className={value > 0 ? 'ops-positive' : 'ops-negative'}>{value > 0 ? '+' : ''}{value.toLocaleString()} 墨水</strong> },
    {
      title: '供应商实际费用',
      dataIndex: 'cost_yuan',
      render: (value?: number, record?: InkRecord) => typeof value === 'number'
        ? <span>¥{value.toFixed(6)}{record?.model ? <><br /><small>{record.model}</small></> : null}</span>
        : '-',
    },
    { title: '操作人', dataIndex: 'operator' },
    { title: '原因', dataIndex: 'reason' },
    { title: '时间', dataIndex: 'createdAt' },
  ];

  return (
    <div className="ops-page">
      <PageHeader title="墨水流水" desc="查询墨水赠送、扣减、冻结、福利领取和 AI 消耗等明细记录。" />

      <Card bordered={false} title="墨水流水" className="ops-panel-card ops-section-row" extra={<Button icon={<ReloadOutlined />} loading={recordsLoading} onClick={loadRecords}>刷新流水</Button>}>
        <Card bordered={false} className="ops-filter-card ops-inner-filter-card">
          <Space wrap>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="用户 / 操作人 / 原因 / 流水号"
              value={recordKeyword}
              onChange={(event) => setRecordKeyword(event.target.value)}
              style={{ width: 260 }}
            />
            <Select
              value={recordType}
              onChange={setRecordType}
              style={{ width: 160 }}
              options={[
                { label: '全部类型', value: 'all' },
                { label: '官方赠送', value: '官方赠送' },
                { label: '官方回收', value: '官方回收' },
                { label: '冻结墨水', value: '冻结墨水' },
                { label: '福利领取', value: '福利领取' },
                { label: 'AI 消耗', value: 'AI消耗' },
              ]}
            />
            <DatePicker.RangePicker
              onChange={(_, dateStrings) => {
                const [start, end] = dateStrings as [string, string];
                setRecordDateRange(start && end ? [start, end] : null);
              }}
            />
            <Button icon={<SearchOutlined />} loading={recordsLoading} onClick={loadRecords}>查询流水</Button>
          </Space>
        </Card>
        <Table rowKey="id" columns={recordColumns} dataSource={records} loading={recordsLoading} pagination={{ pageSize: 8 }} scroll={{ x: 940 }} />
      </Card>
    </div>
  );
};

export const WorkflowMonitorPage: React.FC = () => {
  const [selected, setSelected] = useState<WorkflowProject | null>(null);
  const [stage, setStage] = useState<WorkflowStage | 'all'>('all');
  const [keyword, setKeyword] = useState('');
  const [dataSource, setDataSource] = useState<WorkflowProject[]>([]);
  const [loading, setLoading] = useState(false);

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = buildApiUrl(API_ENDPOINTS.operations.workflows, { stage, keyword });
      const result = await get<ListResponse<WorkflowProject>>(endpoint);
      setDataSource(result.data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载创作流程失败');
    } finally {
      setLoading(false);
    }
  }, [keyword, stage]);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  const columns: ColumnsType<WorkflowProject> = [
    { title: '项目', dataIndex: 'title', fixed: 'left', width: 180, render: (value, record) => <div><strong>{value}</strong><div className="ops-muted">{record.id} · {record.user}</div></div> },
    { title: '当前环节', dataIndex: 'stage', width: 110, render: (value: WorkflowStage) => <Tag color="blue">{value}</Tag> },
    { title: '保存进度', dataIndex: 'savedStages', width: 430, render: (_, record) => <StageDots savedStages={record.savedStages} current={record.stage} /> },
    { title: '完成度', dataIndex: 'progress', width: 150, render: (value: number) => <Progress percent={value} size="small" /> },
    { title: '字数', dataIndex: 'words', width: 110, render: (value: number) => `${value.toLocaleString()} 字` },
    { title: '最近保存', dataIndex: 'lastSaved', width: 120 },
    { title: '风险', dataIndex: 'risk', width: 130, render: (value: string) => <Tag color={value === '正常' ? 'green' : 'orange'}>{value}</Tag> },
    { title: '操作', width: 100, fixed: 'right', render: (_, record) => <Button size="small" icon={<EyeOutlined />} onClick={() => setSelected(record)}>查看</Button> },
  ];

  return (
    <div className="ops-page">
      <PageHeader
        title="创作流程监管"
        desc="按小说项目追踪灵感、设定、人物、大纲、章节、正文的保存状态，方便定位中断、失败和半成品。"
        actions={<Button type="primary" icon={<SafetyCertificateOutlined />} loading={loading} onClick={loadWorkflows}>批量健康检查</Button>}
      />
      <Alert className="ops-tip" type="info" showIcon message="这里已接入真实创作项目，可按用户、小说和流程环节筛选保存状态。" />
      <Card bordered={false} className="ops-filter-card">
        <Space wrap>
          <Input prefix={<SearchOutlined />} placeholder="搜索小说 / 用户" value={keyword} onChange={(event) => setKeyword(event.target.value)} style={{ width: 240 }} />
          <Select
            value={stage}
            onChange={setStage}
            style={{ width: 150 }}
            options={[{ label: '全部环节', value: 'all' }, ...stageOrder.map((item) => ({ label: item, value: item }))]}
          />
          <Select
            defaultValue="all"
            style={{ width: 150 }}
            options={[{ label: '全部状态', value: 'all' }, { label: '已保存', value: 'saved' }, { label: '未保存', value: 'unsaved' }, { label: '生成中断', value: 'failed' }]}
          />
        </Space>
      </Card>
      <Card bordered={false}>
        <Table rowKey="id" columns={columns} dataSource={dataSource} loading={loading} pagination={{ pageSize: 8 }} scroll={{ x: 1280 }} />
      </Card>

      <Drawer title="创作项目详情" width={560} open={!!selected} onClose={() => setSelected(null)}>
        {selected && (
          <div className="ops-drawer-detail">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="小说">{selected.title}</Descriptions.Item>
              <Descriptions.Item label="创作者">{selected.user}</Descriptions.Item>
              <Descriptions.Item label="当前环节">{selected.stage}</Descriptions.Item>
              <Descriptions.Item label="最近保存">{selected.lastSaved}</Descriptions.Item>
              <Descriptions.Item label="风险">{selected.risk}</Descriptions.Item>
              <Descriptions.Item label="真实章节">{selected.chapterCount || 0} 章</Descriptions.Item>
              <Descriptions.Item label="正文字数">{selected.words.toLocaleString()} 字</Descriptions.Item>
              <Descriptions.Item label="摘要">{selected.summary}</Descriptions.Item>
            </Descriptions>
            <h3>流程保存记录</h3>
            <Timeline
              items={stageOrder.map((item) => ({
                color: selected.savedStages.includes(item) ? 'green' : selected.stage === item ? 'blue' : 'gray',
                children: `${item}：${selected.savedStages.includes(item) ? '已保存' : selected.stage === item ? '当前编辑中' : '未开始'}`,
              }))}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
};

export const BookCatalogAdminPage: React.FC = () => {
  const [status, setStatus] = useState<BookStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [storageProvider, setStorageProvider] = useState<BookAdminItem['storageProviderType']>('minio');
  const [keyword, setKeyword] = useState('');
  const [dataSource, setDataSource] = useState<BookAdminItem[]>([]);
  const [categoryStats, setCategoryStats] = useState<BookCategoryStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<LibraryCategoryAdminItem[]>([]);
  const [savingCategoryId, setSavingCategoryId] = useState<number | null>(null);
  const [generatingCoverId, setGeneratingCoverId] = useState<number | null>(null);
  const [paywallBook, setPaywallBook] = useState<BookAdminItem | null>(null);
  const [paywallForm] = Form.useForm();
  const [savingPaywall, setSavingPaywall] = useState(false);
  const [previewBook, setPreviewBook] = useState<BookAdminItem | null>(null);
  const [previewChapters, setPreviewChapters] = useState<BookChapterItem[]>([]);
  const [activeChapter, setActiveChapter] = useState<BookChapterItem | null>(null);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [loadingChapter, setLoadingChapter] = useState(false);
  const [editingChapter, setEditingChapter] = useState(false);
  const [chapterTitle, setChapterTitle] = useState('');
  const [chapterContent, setChapterContent] = useState('');
  const [savingChapter, setSavingChapter] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [uploadTasks, setUploadTasks] = useState<NovelUploadTask[]>([]);
  const [uploading, setUploading] = useState(false);
  const [publishNow, setPublishNow] = useState(false);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [selectedBookIds, setSelectedBookIds] = useState<React.Key[]>([]);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportTask, setExportTask] = useState<BookExportTask | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferFiles, setTransferFiles] = useState<UploadFile[]>([]);
  const [transferResults, setTransferResults] = useState<BookTransferResult[]>([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState('');
  const zipInputRef = useRef<HTMLInputElement>(null);

  const downloadTransferPackage = async (allPublished = false) => {
    if (!allPublished && !selectedBookIds.length) {
      message.warning('请先勾选需要下载的小说');
      return;
    }
    setExporting(true);
    setExportOpen(true);
    setExportTask(null);
    try {
      const task = await request<BookExportTask>(API_ENDPOINTS.operations.exportJobs, {
        method: 'POST',
        body: JSON.stringify({
          project_ids: allPublished ? [] : selectedBookIds.map(Number),
          all_published: allPublished,
        }),
      });
      setExportTask(task);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '创建导出任务失败');
      setExportOpen(false);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!exportOpen || !exportTask?.id) return undefined;
    if (['failed', 'cancelled', 'downloaded'].includes(exportTask.status)) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const task = await get<BookExportTask>(API_ENDPOINTS.operations.exportJob(exportTask.id));
        setExportTask(task);
      } catch (error) {
        window.clearInterval(timer);
        message.error(error instanceof Error ? error.message : '查询导出进度失败');
      }
    }, exportTask.status === 'completed' ? 10000 : 2000);
    return () => window.clearInterval(timer);
  }, [exportOpen, exportTask?.id, exportTask?.status]);

  const importTransferPackage = async () => {
    const selected = transferFiles.map((item) => item.originFileObj).filter(Boolean) as File[];
    if (!selected.length) {
      message.warning('请选择 ZIP 迁移包或解压后的小说目录');
      return;
    }
    const onlyZipFiles = selected.every((file) => file.name.toLowerCase().endsWith('.zip'));
    const batches = onlyZipFiles ? selected.map((file) => [file]) : [selected];
    setImporting(true);
    setTransferResults([]);
    try {
      const allResults: BookTransferResult[] = [];
      for (let index = 0; index < batches.length; index += 1) {
        const batch = batches[index];
        setImportProgress(`正在上传并导入第 ${index + 1}/${batches.length} 个迁移包`);
        const formData = new FormData();
        batch.forEach((file) => {
          const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
          formData.append('files', file, relativePath || file.name);
        });
        const result = await request<{
          data: BookTransferResult[];
          imported: number;
          skipped: number;
          failed: number;
        }>(API_ENDPOINTS.operations.importBooks, { method: 'POST', body: formData });
        allResults.push(...result.data);
        setTransferResults([...allResults]);
      }
      const imported = allResults.filter((item) => item.status === 'imported').length;
      const skipped = allResults.filter((item) => item.status === 'skipped').length;
      const failed = allResults.filter((item) => item.status === 'failed').length;
      message.success(`导入完成：新增 ${imported} 本，跳过 ${skipped} 本，失败 ${failed} 本`);
      await loadBooks();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '导入迁移包失败');
    } finally {
      setImporting(false);
      setImportProgress('');
    }
  };

  const loadUploadTasks = useCallback(async () => {
    try {
      const result = await get<ListResponse<NovelUploadTask>>(API_ENDPOINTS.resourcePull.uploadTasks);
      setUploadTasks(result.data);
      return result.data;
    } catch (error) {
      if (uploadOpen) message.error(error instanceof Error ? error.message : '加载导入队列失败');
      return [];
    }
  }, [uploadOpen]);

  const submitUploads = async () => {
    const selected = uploadFiles.map((item) => item.originFileObj).filter(Boolean) as File[];
    if (!selected.length) {
      message.warning('请先选择 TXT 小说文件');
      return;
    }
    if (!rightsConfirmed) {
      message.warning('请确认已取得作品展示及发布授权');
      return;
    }
    const formData = new FormData();
    selected.forEach((file) => formData.append('files', file));
    formData.append('publish_now', String(publishNow));
    formData.append('rights_confirmed', 'true');
    setUploading(true);
    try {
      await request(API_ENDPOINTS.resourcePull.uploads, { method: 'POST', body: formData });
      message.success(`${selected.length} 本小说已进入处理队列，可继续添加文件`);
      setUploadFiles([]);
      await loadUploadTasks();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const loadBooks = useCallback(async () => {
    setLoading(true);
    try {
      const [selectedChannel, ...selectedCategoryParts] = categoryFilter.split(':');
      const selectedCategory = selectedCategoryParts.join(':');
      const endpoint = buildApiUrl(API_ENDPOINTS.operations.books, {
        status,
        keyword,
        channel: categoryFilter === 'all' ? undefined : selectedChannel,
        category: categoryFilter === 'all' ? undefined : selectedCategory,
      });
      const [booksResult, categoriesResult] = await Promise.all([
        get<ListResponse<BookAdminItem> & {
          storage_provider: BookAdminItem['storageProviderType'];
          category_stats: BookCategoryStat[];
        }>(endpoint),
        get<ListResponse<LibraryCategoryAdminItem>>(API_ENDPOINTS.operations.libraryCategories),
      ]);
      setDataSource(booksResult.data);
      setStorageProvider(booksResult.storage_provider);
      setCategoryStats(booksResult.category_stats || []);
      setCategories(categoriesResult.data.filter((item) => item.enabled));
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载书阁内容失败');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, keyword, status]);

  useEffect(() => {
    if (!uploadOpen) return undefined;
    loadUploadTasks();
    const timer = window.setInterval(async () => {
      const tasks = await loadUploadTasks();
      if (tasks.some((item) => item.status === 'completed')) loadBooks();
    }, 2500);
    return () => window.clearInterval(timer);
  }, [loadBooks, loadUploadTasks, uploadOpen]);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  const selectPreviewChapter = async (book: BookAdminItem, chapter: BookChapterItem) => {
    if (!book.project_id) return;
    setLoadingChapter(true);
    setEditingChapter(false);
    try {
      const detail = await get<BookChapterItem>(
        API_ENDPOINTS.operations.bookChapter(book.project_id, chapter.id),
      );
      setActiveChapter(detail);
      setChapterTitle(detail.title || '');
      setChapterContent(detail.content || '');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '读取章节正文失败');
    } finally {
      setLoadingChapter(false);
    }
  };

  const openBookPreview = async (book: BookAdminItem) => {
    if (!book.project_id) return;
    setPreviewBook(book);
    setPreviewChapters([]);
    setActiveChapter(null);
    setEditingChapter(false);
    setLoadingChapters(true);
    try {
      const result = await get<ListResponse<BookChapterItem>>(
        API_ENDPOINTS.operations.bookChapters(book.project_id),
      );
      setPreviewChapters(result.data);
      if (result.data.length) await selectPreviewChapter(book, result.data[0]);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '读取章节列表失败');
    } finally {
      setLoadingChapters(false);
    }
  };

  const saveChapter = async () => {
    if (!previewBook?.project_id || !activeChapter) return;
    const title = chapterTitle.trim();
    if (!title) {
      message.warning('章节名不能为空');
      return;
    }
    setSavingChapter(true);
    try {
      const updated = await request<BookChapterItem>(
        API_ENDPOINTS.operations.bookChapter(previewBook.project_id, activeChapter.id),
        {
          method: 'PATCH',
          body: JSON.stringify({ title, content: chapterContent }),
        },
      );
      setActiveChapter(updated);
      setChapterTitle(updated.title);
      setChapterContent(updated.content || '');
      setPreviewChapters((items) => items.map((item) => (
        item.id === updated.id
          ? { ...item, title: updated.title, words: updated.words, updated_at: updated.updated_at }
          : item
      )));
      setEditingChapter(false);
      message.success('章节修改已保存到当前云存储');
      await loadBooks();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存章节失败');
    } finally {
      setSavingChapter(false);
    }
  };

  const updateBookStatus = async (record: BookAdminItem, nextStatus: BookStatus) => {
    if (!record.project_id) return;
    try {
      await request(API_ENDPOINTS.operations.bookStatus(record.project_id), {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      });
      message.success(`已更新为${nextStatus}`);
      await loadBooks();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '更新状态失败');
    }
  };

  const updateBookCategory = async (record: BookAdminItem, value: string) => {
    if (!record.project_id) return;
    const [channel, ...categoryParts] = value.split(':');
    const category = categoryParts.join(':');
    if (!category || (channel !== '男频' && channel !== '女频')) return;
    setSavingCategoryId(record.project_id);
    try {
      await request(API_ENDPOINTS.operations.bookCategory(record.project_id), {
        method: 'PATCH',
        body: JSON.stringify({ channel, category }),
      });
      setDataSource((current) => current.map((item) => item.project_id === record.project_id
        ? { ...item, reader: channel === '女频' ? '女生' : '男生', category }
        : item));
      message.success(`已调整到${channel} · ${category}`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '修改分类失败');
    } finally {
      setSavingCategoryId(null);
    }
  };

  const regenerateCover = (record: BookAdminItem) => {
    if (!record.project_id || generatingCoverId !== null) return;
    Modal.confirm({
      title: `AI 更换《${record.title}》封面？`,
      content: '系统将根据书名、分类和简介重新生成竖版封面。生成成功后会立即替换当前封面并同步到用户端。',
      okText: '一键换封面',
      cancelText: '取消',
      async onOk() {
        setGeneratingCoverId(record.project_id as number);
        try {
          const result = await post<{ cover_url: string }>(
            API_ENDPOINTS.operations.regenerateBookCover(record.project_id as number),
          );
          setDataSource((current) => current.map((item) => item.project_id === record.project_id
            ? { ...item, cover: result.cover_url }
            : item));
          message.success('新封面已生成并替换');
        } catch (error) {
          message.error(error instanceof Error ? error.message : 'AI 换封面失败');
          throw error;
        } finally {
          setGeneratingCoverId(null);
        }
      },
    });
  };

  const deleteBook = (record: BookAdminItem) => {
    if (!record.project_id) return;
    Modal.confirm({
      title: '确定要删除这本书吗？',
      content: `删除后「${record.title}」将不再出现在书阁和最近更新中，作者工作台原始内容会保留。`,
      okText: '确定删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      async onOk() {
        try {
          await request(API_ENDPOINTS.operations.deleteBook(record.project_id as number), {
            method: 'DELETE',
          });
          message.success('书阁作品已删除');
          await loadBooks();
        } catch (error) {
          message.error(error instanceof Error ? error.message : '删除书阁作品失败');
        }
      },
    });
  };

  const openPaywall = (record: BookAdminItem) => {
    setPaywallBook(record);
    paywallForm.setFieldsValue({
      enabled: record.paywallEnabled,
      mode: record.paywallMode || 'range',
      paidFromChapter: record.paidFromChapter || 1,
      paidChapterNos: record.paidChapterNos || [],
      chapterPrice: record.chapterPrice || 100,
    });
  };

  const savePaywall = async () => {
    if (!paywallBook?.project_id) return;
    const values = await paywallForm.validateFields();
    setSavingPaywall(true);
    try {
      const result = await request<{ enabled: boolean }>(API_ENDPOINTS.operations.bookPaywall(paywallBook.project_id), {
        method: 'PATCH',
        body: JSON.stringify({
          enabled: values.enabled,
          mode: values.mode,
          paid_from_chapter: values.paidFromChapter,
          paid_chapter_nos: values.paidChapterNos || [],
          chapter_price: values.chapterPrice,
        }),
      });
      message.success(result.enabled ? '章节收费配置已生效' : '作品已切换为全章免费');
      setPaywallBook(null);
      await loadBooks();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存章节收费失败');
    } finally {
      setSavingPaywall(false);
    }
  };

  const columns: ColumnsType<BookAdminItem> = [
    {
      title: '书籍',
      dataIndex: 'title',
      fixed: 'left',
      width: 270,
      render: (_, record) => (
        <div className="ops-book-cell">
          <button
            type="button"
            className="ops-book-cover-action"
            title="点击使用 AI 一键更换封面"
            disabled={!record.project_id || generatingCoverId !== null}
            onClick={() => regenerateCover(record)}
          >
            <img src={record.cover} alt={record.title} />
            <span>{generatingCoverId === record.project_id ? '生成中…' : 'AI 换封面'}</span>
          </button>
          <div>
            <strong>{record.title}</strong>
            <span>{record.author}</span>
          </div>
        </div>
      ),
    },
    {
      title: '存储来源',
      dataIndex: 'storageSource',
      width: 150,
      render: (value: string, record: BookAdminItem) => {
        const colors: Record<BookAdminItem['storageProviderType'], string> = {
          file_service: 'cyan',
          minio: 'blue',
        };
        return (
          <Space direction="vertical" size={2}>
            <Tag color={colors[record.storageProviderType]}>{value}</Tag>
            <span style={{ color: '#8c8c8c', fontSize: 12 }}>{record.storageObjectCount} 个对象</span>
          </Space>
        );
      },
    },
    {
      title: '频道 / 分类',
      dataIndex: 'category',
      width: 210,
      render: (_: string, record: BookAdminItem) => (
        <Select
          value={`${record.reader === '女生' ? '女频' : '男频'}:${record.category}`}
          loading={savingCategoryId === record.project_id}
          disabled={!record.project_id || savingCategoryId === record.project_id}
          style={{ width: '100%' }}
          onChange={(value) => updateBookCategory(record, value)}
          options={(['男频', '女频'] as const).map((channel) => ({
            label: channel,
            options: categories
              .filter((item) => item.channel === channel)
              .map((item) => ({
                label: item.name,
                value: `${channel}:${item.name}`,
              })),
          }))}
        />
      ),
    },
    { title: '状态', dataIndex: 'status', width: 100, render: (value: BookStatus) => <Tag color={bookStatusColor[value]}>{value}</Tag> },
    { title: '字数', dataIndex: 'words', width: 110 },
    { title: '章节', dataIndex: 'chapters', width: 90, render: (value: number) => `${value} 章` },
    { title: '锁章', dataIndex: 'lockedChapters', width: 90, render: (value: number) => <Tag color={value ? 'gold' : 'default'}>{value} 章</Tag> },
    { title: '累计收入', dataIndex: 'income', width: 120, render: (value: number) => <strong>¥{value.toLocaleString()}</strong> },
    { title: 'AI累计消耗', dataIndex: 'aiCost', width: 140, render: (value: number) => <strong>¥{Number(value || 0).toFixed(4)}</strong> },
    { title: '开始创作时间', dataIndex: 'createdAt', width: 160 },
    { title: '最后修改时间', dataIndex: 'updatedAt', width: 160 },
    {
      title: '操作',
      width: 300,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openBookPreview(record)}>预览</Button>
          <Button size="small" icon={<PayCircleOutlined />} onClick={() => openPaywall(record)}>收费</Button>
          <Button size="small" icon={<AuditOutlined />} disabled={record.status !== '审核中'} onClick={() => updateBookStatus(record, '已上架')}>审核</Button>
          {record.status === '已下架' ? (
            <Button size="small" icon={<UploadOutlined />} onClick={() => updateBookStatus(record, '已上架')}>上架</Button>
          ) : (
            <Button size="small" danger icon={<StopOutlined />} onClick={() => updateBookStatus(record, '已下架')}>下架</Button>
          )}
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteBook(record)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="ops-page">
      <PageHeader
        title="书阁内容管理"
        desc="管理前台书阁展示、作品审核、锁定章节和上架状态，也可批量上传 TXT 小说并自动拆分章节。"
        actions={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadBooks}>刷新书阁</Button>
            <Button icon={<DownloadOutlined />} loading={exporting} disabled={!selectedBookIds.length} onClick={() => downloadTransferPackage(false)}>
              下载{selectedBookIds.length ? `（${selectedBookIds.length}）` : ''}
            </Button>
            <Button
              icon={<DownloadOutlined />}
              disabled={exporting}
              onClick={() => Modal.confirm({
                title: '下载全部已上架小说？',
                content: '系统会在后台逐本整理，并按不超过 1GB 自动生成多个压缩包；每本小说只会存在于一个压缩包中。',
                okText: '开始导出全部',
                cancelText: '取消',
                onOk: () => downloadTransferPackage(true),
              })}
            >
              下载全部
            </Button>
            <Button type="primary" icon={<UploadOutlined />} onClick={() => setTransferOpen(true)}>导入</Button>
            <Button icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>TXT 入库</Button>
          </Space>
        )}
      />
      <Row gutter={[16, 16]} className="ops-metric-row">
        <Col xs={24} md={8}>
          <Card bordered={false} className="ops-stat-card"><Statistic title="已上架作品" value={dataSource.filter((item) => item.status === '已上架').length} suffix="本" /></Card>
        </Col>
        <Col xs={24} md={8}>
          <Card bordered={false} className="ops-stat-card"><Statistic title="锁定章节" value={dataSource.reduce((sum, item) => sum + item.lockedChapters, 0)} suffix="章" /></Card>
        </Col>
        <Col xs={24} md={8}>
          <Card bordered={false} className="ops-stat-card"><Statistic title="书阁收入" prefix="¥" value={dataSource.reduce((sum, item) => sum + item.income, 0)} /></Card>
        </Col>
      </Row>
      <Card bordered={false} className="ops-category-stat-card">
        <div className="ops-category-stat-header">
          <div>
            <strong>分类作品统计</strong>
            <span>分类来源于数据库配置，启用、停用或增删后自动同步</span>
          </div>
          <Tag>{categoryStats.reduce((sum, item) => sum + item.count, 0)} 本已归类</Tag>
        </div>
        <div className="ops-category-stat-list">
          {categoryStats.map((item) => {
            const value = `${item.channel}:${item.category}`;
            return (
              <button
                type="button"
                key={item.id}
                className={`ops-category-stat-item${categoryFilter === value ? ' active' : ''}`}
                onClick={() => setCategoryFilter(categoryFilter === value ? 'all' : value)}
              >
                <span>{item.channel} · {item.category}</span>
                <strong>{item.count}</strong>
              </button>
            );
          })}
        </div>
      </Card>
      <Card bordered={false} className="ops-filter-card">
        <Space wrap>
          <Input prefix={<SearchOutlined />} placeholder="搜索书名 / 作者" value={keyword} onChange={(event) => setKeyword(event.target.value)} style={{ width: 240 }} />
          <Select
            value={status}
            onChange={setStatus}
            style={{ width: 150 }}
            options={[{ label: '全部状态', value: 'all' }, ...(['已上架', '审核中', '草稿', '已下架'] as BookStatus[]).map((item) => ({ label: item, value: item }))]}
          />
          <Select
            value={categoryFilter}
            onChange={setCategoryFilter}
            style={{ width: 220 }}
            options={[
              { label: '全部频道 / 分类', value: 'all' },
              ...(['男频', '女频'] as const).map((channel) => ({
                label: channel,
                options: categories
                  .filter((item) => item.channel === channel)
                  .map((item) => {
                    const count = categoryStats.find(
                      (stat) => stat.channel === channel && stat.category === item.name,
                    )?.count || 0;
                    return {
                      label: `${item.name}（${count} 本）`,
                      value: `${channel}:${item.name}`,
                    };
                  }),
              })),
            ]}
          />
          <Tag color={storageProvider === 'file_service' ? 'cyan' : 'blue'}>
            当前资源：{storageProvider === 'file_service' ? '公司云存储' : '内置 MinIO'}
          </Tag>
          <Button icon={<SearchOutlined />} loading={loading} onClick={loadBooks}>筛选</Button>
        </Space>
      </Card>
      <Card bordered={false}>
        <Table
          rowKey="id"
          rowSelection={{
            selectedRowKeys: selectedBookIds,
            onChange: setSelectedBookIds,
            getCheckboxProps: (record) => ({ disabled: !record.project_id }),
          }}
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{ pageSize: 8 }}
          scroll={{ x: 1410 }}
        />
      </Card>
      <Drawer
        title={`章节预览与编辑 · ${previewBook?.title || ''}`}
        width="min(1120px, 96vw)"
        open={Boolean(previewBook)}
        onClose={() => {
          setPreviewBook(null);
          setPreviewChapters([]);
          setActiveChapter(null);
          setEditingChapter(false);
        }}
        destroyOnHidden
      >
        <div className="ops-chapter-workspace">
          <aside className="ops-chapter-list">
            <div className="ops-chapter-list-header">
              <strong>章节目录</strong>
              <Tag>{previewChapters.length} 章</Tag>
            </div>
            {loadingChapters ? (
              <div className="ops-chapter-empty">正在读取章节目录…</div>
            ) : previewChapters.length ? previewChapters.map((chapter) => (
              <button
                type="button"
                key={chapter.id}
                className={`ops-chapter-item${activeChapter?.id === chapter.id ? ' active' : ''}`}
                onClick={() => previewBook && selectPreviewChapter(previewBook, chapter)}
              >
                <span className="ops-chapter-no">第 {chapter.chapter_no} 章</span>
                <strong>{chapter.title}</strong>
                <small>{(chapter.words || 0).toLocaleString()} 字</small>
              </button>
            )) : (
              <div className="ops-chapter-empty">这本书还没有章节</div>
            )}
          </aside>
          <section className="ops-chapter-detail">
            {loadingChapter ? (
              <div className="ops-chapter-empty">正在从云存储读取正文…</div>
            ) : activeChapter ? (
              <>
                <div className="ops-chapter-toolbar">
                  <div>
                    <Tag color="cyan">第 {activeChapter.chapter_no} 章</Tag>
                    <span className="ops-muted">{(activeChapter.words || 0).toLocaleString()} 字</span>
                  </div>
                  <Space>
                    {editingChapter ? (
                      <>
                        <Button
                          onClick={() => {
                            setChapterTitle(activeChapter.title);
                            setChapterContent(activeChapter.content || '');
                            setEditingChapter(false);
                          }}
                        >
                          取消
                        </Button>
                        <Button type="primary" icon={<SaveOutlined />} loading={savingChapter} onClick={saveChapter}>
                          保存修改
                        </Button>
                      </>
                    ) : (
                      <Button icon={<EditOutlined />} onClick={() => setEditingChapter(true)}>编辑本章</Button>
                    )}
                  </Space>
                </div>
                {editingChapter ? (
                  <div className="ops-chapter-editor">
                    <label>
                      <span>章节名称</span>
                      <Input
                        value={chapterTitle}
                        onChange={(event) => setChapterTitle(event.target.value)}
                        maxLength={80}
                        showCount
                      />
                    </label>
                    <label>
                      <span>章节正文</span>
                      <Input.TextArea
                        value={chapterContent}
                        onChange={(event) => setChapterContent(event.target.value)}
                        autoSize={{ minRows: 20, maxRows: 32 }}
                        maxLength={200000}
                        showCount
                      />
                    </label>
                  </div>
                ) : (
                  <article className="ops-chapter-preview">
                    <h2>{activeChapter.title}</h2>
                    <div>{activeChapter.content || '本章暂无正文'}</div>
                  </article>
                )}
              </>
            ) : (
              <div className="ops-chapter-empty">请从左侧选择章节</div>
            )}
          </section>
        </div>
      </Drawer>
      <Modal
        title={`章节收费 · ${paywallBook?.title || ''}`}
        open={Boolean(paywallBook)}
        okText="保存配置"
        cancelText="取消"
        confirmLoading={savingPaywall}
        onOk={savePaywall}
        onCancel={() => setPaywallBook(null)}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          message="可从某章起连续收费，也可指定单章收费。读者解锁后永久可读，同一章节不会重复扣费。"
          style={{ marginBottom: 20 }}
        />
        <Form form={paywallForm} layout="vertical">
          <Form.Item name="enabled" label="收费状态" valuePropName="checked">
            <Switch checkedChildren="按章收费" unCheckedChildren="全章免费" />
          </Form.Item>
          <Form.Item name="mode" label="收费方式" rules={[{ required: true, message: '请选择收费方式' }]}>
            <Select
              options={[
                { value: 'range', label: '从指定章节起连续收费' },
                { value: 'selected', label: '指定章节收费' },
              ]}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item noStyle shouldUpdate={(previous, current) => previous.mode !== current.mode}>
                {({ getFieldValue }) => getFieldValue('mode') === 'selected' ? (
                  <Form.Item
                    name="paidChapterNos"
                    label="选择收费章节"
                    rules={[{ required: true, message: '请至少选择一个收费章节' }]}
                  >
                    <Select
                      mode="multiple"
                      placeholder="选择需要锁定的章节"
                      options={Array.from({ length: paywallBook?.chapters || 0 }, (_, index) => ({
                        value: index + 1,
                        label: `第 ${index + 1} 章`,
                      }))}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="paidFromChapter"
                    label="从第几章开始收费"
                    rules={[{ required: true, message: '请输入收费起始章节' }]}
                  >
                    <InputNumber min={1} max={paywallBook?.chapters || 10000} precision={0} style={{ width: '100%' }} addonAfter="章" />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="chapterPrice"
                label="每章价格（展示墨水）"
                rules={[{ required: true, message: '请输入每章墨水价格' }]}
              >
                <InputNumber min={1} max={1000000} precision={0} style={{ width: '100%' }} addonAfter="墨水" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        title="小说导出进度"
        width={760}
        open={exportOpen}
        footer={<Button onClick={() => setExportOpen(false)}>关闭</Button>}
        onCancel={() => setExportOpen(false)}
        destroyOnHidden={false}
      >
        {exportTask ? (
          <>
            <Progress
              percent={exportTask.progress || 0}
              status={exportTask.status === 'failed' ? 'exception' : exportTask.status === 'completed' || exportTask.status === 'downloaded' ? 'success' : 'active'}
            />
            <Descriptions bordered size="small" column={2} style={{ marginTop: 16 }}>
              <Descriptions.Item label="小说进度">{exportTask.processed_books}/{exportTask.total_books} 本</Descriptions.Item>
              <Descriptions.Item label="章节进度">{exportTask.processed_chapters}/{exportTask.total_chapters || '统计中'} 章</Descriptions.Item>
              <Descriptions.Item label="当前小说" span={2}>{exportTask.current_title || '-'}</Descriptions.Item>
              <Descriptions.Item label="当前状态" span={2}>{exportTask.message}</Descriptions.Item>
            </Descriptions>
            {exportTask.status === 'completed' && (
              <Alert
                type="success"
                showIcon
                style={{ marginTop: 16 }}
                message={`已生成 ${exportTask.parts.length} 个压缩包`}
                description="每本小说只会出现在一个压缩包中。分卷首次下载完成后保留 30 分钟供断线重试，随后自动清理；再次创建导出任务会立即清理本次文件。"
              />
            )}
            {exportTask.status === 'failed' && (
              <Alert type="error" showIcon style={{ marginTop: 16 }} message="导出失败" description={exportTask.message} />
            )}
            {exportTask.parts.length > 0 && (
              <Table
                rowKey="index"
                size="small"
                style={{ marginTop: 16 }}
                pagination={false}
                dataSource={exportTask.parts}
                columns={[
                  { title: '分卷', dataIndex: 'filename' },
                  { title: '小说', dataIndex: 'book_count', width: 90, render: (value: number) => `${value} 本` },
                  { title: '大小', dataIndex: 'size', width: 110, render: (value: number) => `${(value / 1024 / 1024).toFixed(1)} MB` },
                  {
                    title: '操作',
                    width: 150,
                    render: (_, part: BookExportPart) => part.deleted ? (
                      <Tag>已自动清理</Tag>
                    ) : (
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        href={`${buildApiUrl(API_ENDPOINTS.operations.exportPart(exportTask.id, part.index))}?token=${encodeURIComponent(exportTask.download_token)}`}
                        download={part.filename}
                      >
                        {part.downloaded ? '重新下载' : '下载'}
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </>
        ) : (
          <div className="ops-chapter-empty">正在创建后台导出任务…</div>
        )}
      </Modal>
      <Modal
        title="导入完整小说迁移包"
        width={860}
        open={transferOpen}
        footer={null}
        onCancel={() => setTransferOpen(false)}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          message="按书名去重，已有同名作品会自动跳过"
          description="可一次选择下载得到的多个 ZIP，系统会逐包、逐本、逐章串行上传；也可先解压，再拖入或选择包含各小说目录的根目录。"
          style={{ marginBottom: 16 }}
        />
        <Upload.Dragger
          multiple
          directory
          fileList={transferFiles}
          beforeUpload={() => false}
          onChange={({ fileList }) => setTransferFiles(fileList)}
          onRemove={(file) => {
            setTransferFiles((current) => current.filter((item) => item.uid !== file.uid));
            return true;
          }}
        >
          <p className="ant-upload-drag-icon"><UploadOutlined /></p>
          <p className="ant-upload-text">拖拽解压后的迁移目录到这里，或点击选择目录</p>
          <p className="ant-upload-hint">目录中必须保留 manifest.json、cover 和 chapters 文件结构</p>
        </Upload.Dragger>
        <input
          ref={zipInputRef}
          type="file"
          accept=".zip,application/zip"
          multiple
          hidden
          onChange={(event) => {
            const files = Array.from(event.target.files || []);
            setTransferFiles(files.map((file) => ({
              uid: `${file.name}-${file.lastModified}`,
              name: file.name,
              status: 'done',
              originFileObj: file as UploadFile['originFileObj'],
            })));
            event.target.value = '';
          }}
        />
        <div className="ops-upload-options">
          <Button onClick={() => zipInputRef.current?.click()}>选择 ZIP 迁移包</Button>
          <Button type="primary" icon={<UploadOutlined />} loading={importing} disabled={!transferFiles.length} onClick={importTransferPackage}>
            {importProgress || '开始串行导入'}
          </Button>
        </div>
        {transferResults.length > 0 && (
          <Table
            rowKey={(item) => `${item.title}-${item.status}`}
            size="small"
            dataSource={transferResults}
            pagination={false}
            style={{ marginTop: 20 }}
            columns={[
              { title: '小说', dataIndex: 'title' },
              { title: '结果', width: 100, render: (_, item: BookTransferResult) => <Tag color={{ imported: 'success', skipped: 'warning', failed: 'error' }[item.status]}>{({ imported: '已导入', skipped: '已跳过', failed: '失败' } as const)[item.status]}</Tag> },
              { title: '说明', dataIndex: 'message' },
            ]}
          />
        )}
      </Modal>
      <Modal
        title="批量导入 TXT 小说"
        width={860}
        open={uploadOpen}
        footer={null}
        onCancel={() => setUploadOpen(false)}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          message="全程使用本地规则处理，不调用大模型"
          description="支持 UTF-8、GBK/GB18030、Big5 和 UTF-16；自动从文件名提取书名与作者，并按“第X章/节/回/篇、楔子、番外”等独立标题行拆章。任务按顺序排队，处理期间可继续上传下一批。"
          style={{ marginBottom: 16 }}
        />
        <Upload.Dragger
          multiple
          accept=".txt,text/plain"
          fileList={uploadFiles}
          beforeUpload={() => false}
          onChange={({ fileList }) => setUploadFiles(fileList.slice(-30))}
          onRemove={(file) => {
            setUploadFiles((current) => current.filter((item) => item.uid !== file.uid));
            return true;
          }}
        >
          <p className="ant-upload-drag-icon"><UploadOutlined /></p>
          <p className="ant-upload-text">拖拽 TXT 文件到这里，或点击选择</p>
          <p className="ant-upload-hint">单批最多 30 本，单本最大 40MB；可反复添加批次</p>
        </Upload.Dragger>
        <div className="ops-upload-options">
          <Space direction="vertical">
            <Switch checked={publishNow} onChange={setPublishNow} checkedChildren="导入后直接上架" unCheckedChildren="先进入审核中" />
            <label className="ops-rights-confirm">
              <input type="checkbox" checked={rightsConfirmed} onChange={(event) => setRightsConfirmed(event.target.checked)} />
              我确认已取得所上传作品的展示及发布授权，并愿意承担相应版权责任
            </label>
          </Space>
          <Button type="primary" icon={<UploadOutlined />} loading={uploading} disabled={!uploadFiles.length} onClick={submitUploads}>
            上传并开始处理（{uploadFiles.length}）
          </Button>
        </div>
        <h3 className="ops-upload-title">处理队列</h3>
        <Table
          rowKey="id"
          size="small"
          dataSource={uploadTasks}
          pagination={{ pageSize: 6, hideOnSinglePage: true }}
          columns={[
            { title: '文件 / 作品', width: 220, render: (_, item: NovelUploadTask) => <div><strong>{item.title || item.filename}</strong><br /><span className="ops-muted">{item.title ? item.filename : '等待识别'}</span></div> },
            { title: '状态', width: 90, render: (_, item: NovelUploadTask) => <Tag color={{ pending: 'default', running: 'processing', completed: 'success', skipped: 'warning', failed: 'error' }[item.status]}>{({ pending: '排队中', running: '处理中', completed: '已完成', skipped: '已跳过', failed: '失败' } as const)[item.status]}</Tag> },
            { title: '识别结果', width: 170, render: (_, item: NovelUploadTask) => item.chapter_count ? `${item.chapter_count} 章 / ${(item.word_count || 0).toLocaleString()} 字` : item.encoding || '-' },
            { title: '说明', dataIndex: 'message', ellipsis: true },
          ]}
        />
      </Modal>
    </div>
  );
};

export const LibraryRankingAdminPage: React.FC = () => {
  const [items, setItems] = useState<LibraryRankingAdminItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [channel, setChannel] = useState<'男频' | '女频' | '新书'>('男频');

  const loadRankings = useCallback(async () => {
    setLoading(true);
    try {
      const result = await get<ListResponse<LibraryRankingAdminItem>>(API_ENDPOINTS.operations.libraryRankings);
      setItems(result.data.map((item) => ({ ...item, channel: item.channel || '男频' })));
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载巅峰榜失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRankings();
  }, [loadRankings]);

  const updateItem = <K extends keyof LibraryRankingAdminItem>(index: number, key: K, value: LibraryRankingAdminItem[K]) => {
    setItems((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)));
  };

  const saveRankings = async () => {
    if (!items.length) {
      message.warning('至少保留一条榜单作品');
      return;
    }
    setLoading(true);
    try {
      await request(API_ENDPOINTS.operations.libraryRankings, {
        method: 'PUT',
        body: JSON.stringify({ items }),
      });
      message.success('巅峰榜已保存');
      await loadRankings();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存巅峰榜失败');
    } finally {
      setLoading(false);
    }
  };
  const visibleItems = useMemo(() => items.filter((item) => item.channel === channel), [channel, items]);
  const enabledCount = visibleItems.filter((item) => item.enabled).length;

  const columns: ColumnsType<LibraryRankingAdminItem> = [
    {
      title: '排名',
      dataIndex: 'rank',
      width: 80,
      render: (value) => <strong>#{value}</strong>,
    },
    {
      title: '榜单分类',
      dataIndex: 'channel',
      width: 100,
      render: (value, record) => value === '新书' ? <Tag color="green">新书</Tag> : (
        <Select
          value={value}
          style={{ width: 92 }}
          options={[{ label: '男频', value: '男频' }, { label: '女频', value: '女频' }]}
          onChange={(nextChannel) => {
            const index = items.findIndex((item) => item.id === record.id);
            if (index >= 0) updateItem(index, 'channel', nextChannel);
          }}
        />
      ),
    },
    {
      title: '作品标题',
      dataIndex: 'title',
      width: 360,
      render: (_, record) => (
        <Space>
          <Avatar shape="square" size={52} src={record.cover_url} />
          <div><strong>{record.title}</strong><br /><span className="ops-muted">{record.author || '未知作者'} · {record.slug}</span></div>
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (value) => <Tag color="success">{value}</Tag>,
    },
    {
      title: '更新时间',
      dataIndex: 'update',
      width: 180,
      render: (value) => value || '-',
    },
    {
      title: '综合数据',
      width: 190,
      render: (_, record) => (
        <div>
          <strong>综合分 {record.score || 0}</strong><br />
          <span className="ops-muted">阅读 {record.read_count || 0} · 点赞 {record.like_count || 0} · 收藏 {record.favorite_count || 0}</span>
        </div>
      ),
    },
    {
      title: '前台展示',
      dataIndex: 'enabled',
      width: 120,
      render: (_, record) => (
        <Switch
          checked={record.enabled}
          checkedChildren="展示"
          unCheckedChildren="隐藏"
          onChange={(value) => {
            const index = items.findIndex((item) => item.id === record.id);
            if (index >= 0) updateItem(index, 'enabled', value);
          }}
        />
      ),
    },
  ];

  return (
    <div className="ops-page">
      <PageHeader
        title="巅峰榜管理"
        desc="榜单自动读取全部真实已上架作品，按阅读、点赞、收藏、章节量和近期活跃度计算综合分；前台每个频道自动展示前 27 本。"
        actions={(
          <Space>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={loadRankings}>刷新</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={saveRankings}>保存全部</Button>
          </Space>
        )}
      />
      <Alert
        className="ops-tip"
        type="info"
        showIcon
        message={`书库现有榜单候选 ${items.length} 条；${channel}频道 ${visibleItems.length} 条，已启用 ${enabledCount} 条。新上架作品会自动加入并按综合分排序；隐藏某本后，后续作品会自动补位，无需人工添加。`}
      />
      <Card bordered={false}>
        <Space style={{ marginBottom: 18 }}>
          <span>查看频道：</span>
          <Select
            value={channel}
            style={{ width: 120 }}
            options={[{ label: '男频', value: '男频' }, { label: '女频', value: '女频' }, { label: '新书', value: '新书' }]}
            onChange={setChannel}
          />
        </Space>
        <Table
          rowKey={(record, index) => record.id || `${record.title}-${index}`}
          columns={columns}
          dataSource={visibleItems}
          loading={loading}
          pagination={{ pageSize: 9, hideOnSinglePage: true }}
          scroll={{ x: 980 }}
        />
      </Card>
    </div>
  );
};

const librarySectionLabels: Record<LibrarySectionKey, string> = {
  male_featured: '男频精选',
  female_featured: '女频精选',
  copyright: '版权改编',
  activity_wall: '活动图书墙',
};

export const LibrarySectionAdminPage: React.FC = () => {
  const [items, setItems] = useState<LibrarySectionAdminItem[]>([]);
  const [books, setBooks] = useState<BookAdminItem[]>([]);
  const [sectionFilter, setSectionFilter] = useState<'all' | LibrarySectionKey>('all');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sectionResult, bookResult] = await Promise.all([
        get<ListResponse<LibrarySectionAdminItem>>(API_ENDPOINTS.operations.librarySections),
        get<ListResponse<BookAdminItem>>(buildApiUrl(API_ENDPOINTS.operations.books, { status: '已上架' })),
      ]);
      setItems(sectionResult.data || []);
      setBooks((bookResult.data || []).filter((book) => book.slug));
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载书阁版块失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateItem = <K extends keyof LibrarySectionAdminItem>(record: LibrarySectionAdminItem, key: K, value: LibrarySectionAdminItem[K]) => {
    setItems((current) => current.map((item) => item.id === record.id ? { ...item, [key]: value } : item));
  };

  const addItem = () => {
    const section: LibrarySectionKey = sectionFilter === 'all' ? 'male_featured' : sectionFilter;
    const sort = items.filter((item) => item.section === section).length + 1;
    const book = books.find((candidate) => !items.some((item) => item.section === section && item.slug === candidate.slug));
    setItems((current) => [...current, {
      id: `section-${Date.now()}`,
      section,
      sort,
      slug: book?.slug || '',
      enabled: true,
      title: book?.title,
      author: book?.author,
      cover_url: book?.cover,
      available: Boolean(book),
    }]);
  };

  const removeItem = (record: LibrarySectionAdminItem) => {
    Modal.confirm({
      title: `从“${librarySectionLabels[record.section]}”移除这条展示记录？`,
      content: '这里只会移除当前板块的展示配置，不会删除作品、章节或正文。点击“保存全部”后正式生效。',
      okText: '仅移出板块',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => setItems((current) => current.filter((item) => item.id !== record.id)),
    });
  };

  const save = async () => {
    if (items.some((item) => !item.slug)) {
      message.warning('每一项都必须选择已上架作品');
      return;
    }
    const unavailableCount = items.filter((item) => item.available === false).length;
    setLoading(true);
    try {
      await request<{ skipped_unavailable?: string[] }>(API_ENDPOINTS.operations.librarySections, {
        method: 'PUT',
        body: JSON.stringify({ items: items.map(({ id, section, sort, slug, enabled }) => ({ id, section, sort, slug, enabled })) }),
      });
      if (unavailableCount) {
        message.success(`书阁版块配置已保存，并清理 ${unavailableCount} 条失效作品记录`);
      } else {
        message.success('书阁版块配置已保存');
      }
      await load();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存失败');
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<LibrarySectionAdminItem> = [
    {
      title: '展示版块',
      dataIndex: 'section',
      width: 170,
      render: (_, record) => (
        <Select
          value={record.section}
          style={{ width: 145 }}
          options={(Object.keys(librarySectionLabels) as LibrarySectionKey[]).map((value) => ({ value, label: librarySectionLabels[value] }))}
          onChange={(value) => updateItem(record, 'section', value)}
        />
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort',
      width: 90,
      render: (_, record) => <InputNumber min={1} max={99} value={record.sort} onChange={(value) => updateItem(record, 'sort', Number(value || 1))} />,
    },
    {
      title: '选择已上架作品',
      dataIndex: 'slug',
      width: 380,
      render: (_, record) => (
        <Select
          showSearch
          optionFilterProp="label"
          value={record.slug || undefined}
          style={{ width: 350 }}
          options={books.map((book) => ({ value: book.slug, label: `${book.title} · ${book.author}` }))}
          onChange={(slug) => {
            const book = books.find((item) => item.slug === slug);
            setItems((current) => current.map((item) => item.id === record.id
              ? { ...item, slug, title: book?.title, author: book?.author, cover_url: book?.cover, available: true }
              : item));
          }}
        />
      ),
    },
    {
      title: '作品',
      render: (_, record) => (
        <Space>
          {record.cover_url && <Avatar shape="square" size={44} src={record.cover_url} />}
          <span><strong>{record.title || '未选择'}</strong><br /><span className="ops-muted">{record.author || record.slug}</span></span>
        </Space>
      ),
    },
    {
      title: '上下架',
      dataIndex: 'enabled',
      width: 110,
      render: (_, record) => <Switch checked={record.enabled} checkedChildren="展示" unCheckedChildren="隐藏" onChange={(value) => updateItem(record, 'enabled', value)} />,
    },
    {
      title: '操作',
      width: 90,
      render: (_, record) => <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeItem(record)}>移出板块</Button>,
    },
  ];

  const filteredItems = sectionFilter === 'all'
    ? items
    : items.filter((item) => item.section === sectionFilter);
  const unavailableCount = items.filter((item) => item.available === false).length;

  return (
    <div className="ops-page">
      <PageHeader
        title="书阁版块管理"
        desc="统一管理男频精选、女频精选、版权改编和活动图书墙。作品下架后会自动从前台隐藏。"
        actions={<Space><Button icon={<ReloadOutlined />} onClick={load}>刷新</Button><Button icon={<PlusOutlined />} onClick={addItem}>新增到当前板块</Button><Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={save}>保存全部</Button></Space>}
      />
      <Alert
        className="ops-tip"
        type={unavailableCount ? 'warning' : 'info'}
        showIcon
        message={unavailableCount
          ? `发现 ${unavailableCount} 条已下架或不存在的历史配置，保存时会自动清理，不会影响其他记录。`
          : '只允许选择已上架作品；可调整版块、排序和展示状态。移出板块不会删除作品、章节或正文。'}
      />
      <Card bordered={false}>
        <Space style={{ marginBottom: 16 }}>
          <span>按板块查看：</span>
          <Select
            value={sectionFilter}
            style={{ width: 180 }}
            options={[
              { value: 'all', label: `全部板块（${items.length}）` },
              ...(Object.keys(librarySectionLabels) as LibrarySectionKey[]).map((value) => ({
                value,
                label: `${librarySectionLabels[value]}（${items.filter((item) => item.section === value).length}）`,
              })),
            ]}
            onChange={setSectionFilter}
          />
        </Space>
        <Table rowKey={(record, index) => record.id || `${record.section}-${index}`} columns={columns} dataSource={filteredItems} loading={loading} pagination={{ pageSize: 12 }} scroll={{ x: 1100 }} />
      </Card>
    </div>
  );
};

export const SiteNewsAdminPage: React.FC = () => {
  const [items, setItems] = useState<SiteNewsAdminItem[]>([]);
  const [loading, setLoading] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  const loadNews = useCallback(async () => {
    setLoading(true);
    try {
      const result = await get<ListResponse<SiteNewsAdminItem>>(API_ENDPOINTS.operations.siteNews);
      setItems(result.data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载站内动态失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const updateItem = <K extends keyof SiteNewsAdminItem>(index: number, key: K, value: SiteNewsAdminItem[K]) => {
    setItems((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)));
  };

  const addItem = () => {
    const sort = items.length + 1;
    setItems((current) => [...current, { id: `news-${Date.now()}`, sort, date: '今天', title: '新的站内动态', desc: '', content: '', enabled: true }]);
  };

  const removeItem = (index: number) => {
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index).map((item, itemIndex) => ({ ...item, sort: itemIndex + 1 })));
  };

  const exportNews = () => {
    const payload = JSON.stringify({
      version: 1,
      exported_at: new Date().toISOString(),
      items,
    }, null, 2);
    const url = URL.createObjectURL(new Blob([payload], { type: 'application/json;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `lingbi-news-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    message.success(`已导出 ${items.length} 条站内动态`);
  };

  const importNews = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const parsed: unknown = JSON.parse(await file.text());
      const source = Array.isArray(parsed)
        ? parsed
        : (parsed && typeof parsed === 'object' && Array.isArray((parsed as { items?: unknown }).items)
          ? (parsed as { items: unknown[] }).items
          : null);
      if (!source) throw new Error('文件中缺少 items 数组');
      if (!source.length) throw new Error('导入文件没有资讯内容');
      if (source.length > 12) throw new Error('最多只能导入 12 条资讯');

      const imported = source.map((raw, index) => {
        if (!raw || typeof raw !== 'object') throw new Error(`第 ${index + 1} 条格式不正确`);
        const item = raw as Partial<SiteNewsAdminItem>;
        const title = String(item.title || '').trim();
        if (!title) throw new Error(`第 ${index + 1} 条缺少标题`);
        return {
          id: String(item.id || `news-${Date.now()}-${index}`),
          sort: Number.isFinite(Number(item.sort)) ? Number(item.sort) : index + 1,
          date: String(item.date || '今天').trim() || '今天',
          title,
          desc: String(item.desc || '').trim(),
          content: String(item.content || item.desc || '').trim(),
          enabled: item.enabled !== false,
        };
      });

      setItems(imported);
      message.success(`已导入 ${imported.length} 条，请确认后点击“保存全部”`);
    } catch (error) {
      message.error(error instanceof Error ? `导入失败：${error.message}` : '导入失败，请检查文件格式');
    }
  };

  const saveNews = async () => {
    if (!items.length) {
      message.warning('至少保留一条站内动态');
      return;
    }
    const invalid = items.find((item) => !String(item.title || '').trim());
    if (invalid) {
      message.warning('动态标题不能为空');
      return;
    }
    setLoading(true);
    try {
      await request(API_ENDPOINTS.operations.siteNews, {
        method: 'PUT',
        body: JSON.stringify({ items }),
      });
      message.success('站内动态已保存');
      await loadNews();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存站内动态失败');
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<SiteNewsAdminItem> = [
    {
      title: '排序',
      dataIndex: 'sort',
      width: 90,
      render: (_, record, index) => <InputNumber min={1} max={99} precision={0} value={record.sort} onChange={(value) => updateItem(index, 'sort', Number(value || index + 1))} />,
    },
    {
      title: '日期',
      dataIndex: 'date',
      width: 120,
      render: (_, record, index) => <Input value={record.date} placeholder="06-09" onChange={(event) => updateItem(index, 'date', event.target.value)} />,
    },
    {
      title: '标题',
      dataIndex: 'title',
      width: 260,
      render: (_, record, index) => <Input value={record.title} onChange={(event) => updateItem(index, 'title', event.target.value)} />,
    },
    {
      title: '说明',
      dataIndex: 'desc',
      render: (_, record, index) => <Input.TextArea rows={2} value={record.desc} onChange={(event) => updateItem(index, 'desc', event.target.value)} />,
    },
    {
      title: '资讯正文',
      dataIndex: 'content',
      width: 420,
      render: (_, record, index) => <Input.TextArea rows={4} maxLength={4000} value={record.content || ''} placeholder="点击资讯后展示的完整正文" onChange={(event) => updateItem(index, 'content', event.target.value)} />,
    },
    {
      title: '前台展示',
      dataIndex: 'enabled',
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.enabled ? 'yes' : 'no'}
          style={{ width: 94 }}
          options={[{ label: '展示', value: 'yes' }, { label: '隐藏', value: 'no' }]}
          onChange={(value) => updateItem(index, 'enabled', value === 'yes')}
        />
      ),
    },
    {
      title: '操作',
      width: 90,
      fixed: 'right',
      render: (_, __, index) => <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeItem(index)}>删除</Button>,
    },
  ];

  return (
    <div className="ops-page">
      <PageHeader
        title="最新资讯"
        desc="维护书阁首页右侧“最新资讯”。前台自动读取排序靠前且启用的 9 条，与左侧巅峰榜高度对齐。"
        actions={(
          <Space>
            <input ref={importInputRef} type="file" accept=".json,application/json" hidden onChange={importNews} />
            <Button icon={<ReloadOutlined />} loading={loading} onClick={loadNews}>刷新</Button>
            <Button icon={<DownloadOutlined />} disabled={!items.length} onClick={exportNews}>下载全部</Button>
            <Button icon={<UploadOutlined />} onClick={() => importInputRef.current?.click()}>上传导入</Button>
            <Button icon={<PlusOutlined />} onClick={addItem}>新增</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={saveNews}>保存全部</Button>
          </Space>
        )}
      />
      <Alert className="ops-tip" type="info" showIcon message="上传支持本站导出的 JSON 文件；导入仅替换当前编辑列表，点击“保存全部”后才会正式生效。" />
      <Card bordered={false}>
        <Table rowKey={(record, index) => record.id || `${record.title}-${index}`} columns={columns} dataSource={items} loading={loading} pagination={{ pageSize: 9 }} scroll={{ x: 1500 }} />
      </Card>
    </div>
  );
};

export const BookshelfAdminPage: React.FC = () => {
  const [items, setItems] = useState<BookshelfAdminItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ keyword: keyword.trim(), page: '1', page_size: '100' });
      const result = await get<ListResponse<BookshelfAdminItem>>(`${API_ENDPOINTS.operations.bookshelves}?${query}`);
      setItems(result.data);
      setTotal(result.total);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载用户书架失败');
    } finally {
      setLoading(false);
    }
  }, [keyword]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: ColumnsType<BookshelfAdminItem> = [
    {
      title: '用户',
      render: (_, record) => <span><strong>{record.username}</strong><br /><span className="ops-muted">ID {record.user_id}</span></span>,
    },
    {
      title: '收藏作品',
      render: (_, record) => (
        <Space>
          {record.cover && <Avatar shape="square" size={48} src={record.cover} />}
          <span><strong>{record.title}</strong><br /><span className="ops-muted">{record.slug}</span></span>
        </Space>
      ),
    },
    {
      title: '加入时间',
      dataIndex: 'added_at',
      width: 190,
      render: (value) => value ? new Date(value).toLocaleString('zh-CN') : '-',
    },
  ];

  return (
    <div className="ops-page">
      <PageHeader
        title="用户书架"
        desc={`查看用户加入书架的作品，当前共 ${total} 条收藏记录。收藏数会同步参与书阁榜单计算。`}
        actions={<Space><Input allowClear value={keyword} prefix={<SearchOutlined />} placeholder="搜索用户或作品" onChange={(event) => setKeyword(event.target.value)} onPressEnter={() => void load()} /><Button icon={<ReloadOutlined />} onClick={() => void load()}>刷新</Button></Space>}
      />
      <Card bordered={false}>
        <Table rowKey="id" columns={columns} dataSource={items} loading={loading} pagination={{ pageSize: 20 }} />
      </Card>
    </div>
  );
};

export const LibraryCategoryAdminPage: React.FC = () => {
  const [items, setItems] = useState<LibraryCategoryAdminItem[]>([]);
  const [loading, setLoading] = useState(false);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const result = await get<ListResponse<LibraryCategoryAdminItem>>(API_ENDPOINTS.operations.libraryCategories);
      setItems(result.data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载分类失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  const updateItem = <K extends keyof LibraryCategoryAdminItem>(index: number, key: K, value: LibraryCategoryAdminItem[K]) => {
    setItems((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)));
  };

  const addItem = () => {
    const channel: LibraryCategoryAdminItem['channel'] = '男频';
    const sort = items.filter((item) => item.channel === channel).length + 1;
    setItems((current) => [...current, { id: `category-${Date.now()}`, sort, channel, name: '新分类', enabled: true }]);
  };

  const saveCategories = async () => {
    if (!items.some((item) => item.enabled && item.channel === '男频') || !items.some((item) => item.enabled && item.channel === '女频')) {
      message.warning('男频和女频至少各保留一个启用分类');
      return;
    }
    if (items.some((item) => !item.name.trim())) {
      message.warning('分类名称不能为空');
      return;
    }
    setLoading(true);
    try {
      await request(API_ENDPOINTS.operations.libraryCategories, { method: 'PUT', body: JSON.stringify({ items }) });
      message.success('书库分类已保存');
      await loadCategories();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存分类失败');
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<LibraryCategoryAdminItem> = [
    {
      title: '频道', dataIndex: 'channel', width: 130,
      render: (_, record, index) => <Select value={record.channel} options={[{ label: '男频', value: '男频' }, { label: '女频', value: '女频' }]} onChange={(value) => updateItem(index, 'channel', value)} />,
    },
    {
      title: '排序', dataIndex: 'sort', width: 110,
      render: (_, record, index) => <InputNumber min={1} max={99} precision={0} value={record.sort} onChange={(value) => updateItem(index, 'sort', Number(value || 1))} />,
    },
    {
      title: '分类名称', dataIndex: 'name',
      render: (_, record, index) => <Input value={record.name} maxLength={30} onChange={(event) => updateItem(index, 'name', event.target.value)} />,
    },
    {
      title: '启用', dataIndex: 'enabled', width: 110,
      render: (_, record, index) => <Switch checked={record.enabled} onChange={(value) => updateItem(index, 'enabled', value)} />,
    },
    {
      title: '操作', width: 100,
      render: (_, __, index) => <Button danger size="small" icon={<DeleteOutlined />} onClick={() => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))}>删除</Button>,
    },
  ];

  return (
    <div className="ops-page">
      <PageHeader
        title="分类管理"
        desc="统一维护男频、女频分类，供书库筛选、作品发布和初始化数据任务共同使用。"
        actions={<Space><Button icon={<ReloadOutlined />} loading={loading} onClick={loadCategories}>刷新</Button><Button icon={<PlusOutlined />} onClick={addItem}>新增</Button><Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={saveCategories}>保存全部</Button></Space>}
      />
      <Alert className="ops-tip" type="info" showIcon message="修改后前台分类会同步更新；已发布作品保留原分类，后续发布将使用当前启用分类。" />
      <Card bordered={false}>
        <Table rowKey={(record, index) => record.id || `${record.channel}-${record.name}-${index}`} columns={columns} dataSource={items} loading={loading} pagination={false} />
      </Card>
    </div>
  );
};

type CampaignType = 'plus_lottery';

interface CampaignAdminItem {
  id?: string;
  sort: number;
  type: CampaignType;
  name: string;
  nav_label: string;
  phase_label: string;
  badge: string;
  headline_prefix: string;
  headline_main: string;
  original_price: string;
  current_price: string;
  countdown_label: string;
  countdown_value: string;
  win_rate: string;
  prize_title: string;
  prize_price: string;
  features: string[];
  participant_text: string;
  claim_note: string;
  claim_source: string;
  enabled: boolean;
}

const createCampaign = (sort: number): CampaignAdminItem => ({
  id: `campaign-${Date.now()}`,
  sort,
  type: 'plus_lottery',
  name: '新的福利活动',
  nav_label: '福利',
  phase_label: '第 1 期',
  badge: '福利 · 20:00 开启',
  headline_prefix: '全网抽 3,000 名作者',
  headline_main: '免费领 Plus 年费会员',
  original_price: '¥468',
  current_price: '¥0',
  countdown_label: '距离开奖',
  countdown_value: '9 天 06:46:41',
  win_rate: '13.0%',
  prize_title: '奖品 · 3,000 份 Plus 年费会员',
  prize_price: '¥468/年 → ¥0',
  features: ['无限本数生成 / 单本 300 章 60 万字', '顶级模型 · 全部后期工具', '商用授权 · 番茄 / 晋江 / 起点 / 七猫 可投'],
  participant_text: '已有 23,100 位作者参与',
  claim_note: '每个账号限领 1 次',
  claim_source: 'welfare',
  enabled: false,
});

export const CampaignAdminPage: React.FC = () => {
  const [items, setItems] = useState<CampaignAdminItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<CampaignAdminItem | null>(null);
  const [featureText, setFeatureText] = useState('');

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const result = await get<ListResponse<CampaignAdminItem>>(API_ENDPOINTS.operations.campaigns);
      setItems(result.data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载活动配置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const updateItem = <K extends keyof CampaignAdminItem>(index: number, key: K, value: CampaignAdminItem[K]) => {
    if (key === 'enabled' && value === true && items.some((item, itemIndex) => item.enabled && itemIndex !== index)) {
      message.warning('已有一个活动正在开启，同一时间只能开启一个活动');
      return;
    }
    setItems((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)));
  };

  const addItem = () => {
    setItems((current) => [...current, createCampaign(current.length + 1)]);
  };

  const removeItem = (index: number) => {
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index).map((item, itemIndex) => ({ ...item, sort: itemIndex + 1 })));
  };

  const openEditor = (index: number) => {
    const current = items[index];
    setEditingIndex(index);
    setDraft({ ...current, features: [...current.features] });
    setFeatureText((current.features || []).join('\n'));
  };

  const closeEditor = () => {
    setEditingIndex(null);
    setDraft(null);
    setFeatureText('');
  };

  const updateDraft = <K extends keyof CampaignAdminItem>(key: K, value: CampaignAdminItem[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const applyDraft = () => {
    if (editingIndex === null || !draft) return;
    const nextDraft = {
      ...draft,
      features: featureText.split('\n').map((line) => line.trim()).filter(Boolean),
    };
    if (!nextDraft.name.trim() || !nextDraft.headline_main.trim()) {
      message.warning('活动名称和主标题不能为空');
      return;
    }
    if (nextDraft.enabled && items.some((item, itemIndex) => item.enabled && itemIndex !== editingIndex)) {
      message.warning('已有一个活动正在开启，同一时间只能开启一个活动');
      return;
    }
    setItems((current) => current.map((item, itemIndex) => (itemIndex === editingIndex ? nextDraft : item)));
    closeEditor();
  };

  const saveCampaigns = async () => {
    if (!items.length) {
      message.warning('至少保留一个活动配置');
      return;
    }
    if (items.filter((item) => item.enabled).length > 1) {
      message.warning('已有一个活动正在开启，同一时间只能开启一个活动');
      return;
    }
    const invalid = items.find((item) => !String(item.name || '').trim() || !String(item.headline_main || '').trim());
    if (invalid) {
      message.warning('活动名称和主标题不能为空');
      return;
    }
    setLoading(true);
    try {
      await request(API_ENDPOINTS.operations.campaigns, {
        method: 'PUT',
        body: JSON.stringify({ items }),
      });
      message.success('活动配置已保存');
      await loadCampaigns();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存活动配置失败');
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<CampaignAdminItem> = [
    {
      title: '排序',
      dataIndex: 'sort',
      width: 90,
      render: (_, record, index) => <InputNumber min={1} max={99} precision={0} value={record.sort} onChange={(value) => updateItem(index, 'sort', Number(value || index + 1))} />,
    },
    {
      title: '活动',
      dataIndex: 'name',
      width: 260,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <strong>{record.name}</strong>
          <span className="ops-muted">{record.headline_prefix} · {record.headline_main}</span>
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      width: 150,
      render: () => <Tag color="green">Plus 抽奖</Tag>,
    },
    {
      title: '导航',
      dataIndex: 'nav_label',
      width: 120,
      render: (_, record, index) => <Input value={record.nav_label} onChange={(event) => updateItem(index, 'nav_label', event.target.value)} />,
    },
    {
      title: '前台状态',
      dataIndex: 'enabled',
      width: 130,
      render: (_, record, index) => (
        <Select
          value={record.enabled ? 'enabled' : 'disabled'}
          style={{ width: 108 }}
          options={[{ label: '开启', value: 'enabled' }, { label: '隐藏', value: 'disabled' }]}
          onChange={(value) => updateItem(index, 'enabled', value === 'enabled')}
        />
      ),
    },
    {
      title: '操作',
      width: 150,
      fixed: 'right',
      render: (_, __, index) => (
        <Space>
          <Button size="small" icon={<FileSearchOutlined />} onClick={() => openEditor(index)}>编辑</Button>
          <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeItem(index)}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="ops-page">
      <PageHeader
        title="活动配置"
        desc="控制官网顶部福利入口和活动横幅。同一时间只能开启一个活动；全部隐藏时，前台不会展示福利导航和活动区。"
        actions={(
          <Space>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={loadCampaigns}>刷新</Button>
            <Button icon={<PlusOutlined />} onClick={addItem}>新增活动</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={saveCampaigns}>保存全部</Button>
          </Space>
        )}
      />
      <Alert className="ops-tip" type="info" showIcon message="当前模板支持 Plus 抽奖。后续新增活动时，只要补充新的前台模板，再在这里增加配置即可。" />
      <Card bordered={false}>
        <Table rowKey={(record, index) => record.id || `${record.name}-${index}`} columns={columns} dataSource={items} loading={loading} pagination={{ pageSize: 8 }} scroll={{ x: 1020 }} />
      </Card>

      <Drawer
        width={720}
        title="编辑活动"
        open={draft !== null}
        onClose={closeEditor}
        extra={(
          <Space>
            <Button onClick={closeEditor}>取消</Button>
            <Button type="primary" onClick={applyDraft}>应用</Button>
          </Space>
        )}
      >
        {draft && (
          <Form layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="活动名称" required>
                  <Input value={draft.name} onChange={(event) => updateDraft('name', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="活动类型">
                  <Select value={draft.type} options={[{ label: 'Plus 抽奖', value: 'plus_lottery' }]} onChange={(value) => updateDraft('type', value)} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="导航文案">
                  <Input value={draft.nav_label} onChange={(event) => updateDraft('nav_label', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="期数角标">
                  <Input value={draft.phase_label} onChange={(event) => updateDraft('phase_label', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="顶部标签">
                  <Input value={draft.badge} onChange={(event) => updateDraft('badge', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="副标题">
                  <Input value={draft.headline_prefix} onChange={(event) => updateDraft('headline_prefix', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="主标题" required>
                  <Input value={draft.headline_main} onChange={(event) => updateDraft('headline_main', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="原价">
                  <Input value={draft.original_price} onChange={(event) => updateDraft('original_price', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="当前价">
                  <Input value={draft.current_price} onChange={(event) => updateDraft('current_price', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="中奖率">
                  <Input value={draft.win_rate} onChange={(event) => updateDraft('win_rate', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="倒计时标题">
                  <Input value={draft.countdown_label} onChange={(event) => updateDraft('countdown_label', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item label="倒计时文案">
                  <Input value={draft.countdown_value} onChange={(event) => updateDraft('countdown_value', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="奖品标题">
                  <Input value={draft.prize_title} onChange={(event) => updateDraft('prize_title', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="奖品价格">
                  <Input value={draft.prize_price} onChange={(event) => updateDraft('prize_price', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="参与人数文案">
                  <Input value={draft.participant_text} onChange={(event) => updateDraft('participant_text', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="领取限制">
                  <Input value={draft.claim_note} onChange={(event) => updateDraft('claim_note', event.target.value)} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="权益说明">
                  <Input.TextArea rows={4} value={featureText} onChange={(event) => setFeatureText(event.target.value)} placeholder="每行一条权益" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Drawer>
    </div>
  );
};
